import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/Sidebar';
import { DashboardSidebar } from './DashboardSidebar';
import { DashboardContentChrome } from './DashboardContentChrome';
import { DashboardMobileTabBar } from './DashboardMobileTabBar';
import { PageHeaderProvider, PageHeaderRouteReset, usePageHeaderContext } from './PageHeaderContext';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';
import {
  getCoachMobileTabItems,
  getGuardianMobileTabItems,
} from './mobile-tab-nav-config';
import { isOrganizationManagementRole } from './sidebar-nav-config';
import { useUnifiedManagementNav } from '@/hooks/useUnifiedManagementNav';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { ClubManagementTourHost } from '@/components/club/ClubManagementTourHost';
import { PageTourHost } from '@/components/tour/PageTourHost';
import { cn } from '@/lib/utils';
import { useCurrentAcademy } from '@/hooks/useDataStore';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { fetchOwnerStripeTierQuery } from '@/queries/academyOwnerStripeTier';

function InCardHeaderSlot() {
  const { setInCardHeaderSlotEl } = usePageHeaderContext();
  return <div ref={setInCardHeaderSlotEl} className="shrink-0" />;
}

interface DashboardLayoutProps {
  dashboardRole:
    | 'academy_owner'
    | 'academy_admin'
    | 'club_owner'
    | 'club_admin'
    | 'guardian'
    | 'admin'
    | 'coach';
  academySlug?: string;
  academyName?: string;
  /** Academy logo - only shown in guardian sidebar when set. Falls back to guardianAcademy.logoUrl when not provided. */
  academyLogoUrl?: string | null;
  clubName?: string;
  clubLogoUrl?: string | null;
  children?: React.ReactNode;
}

export function DashboardLayout({
  dashboardRole,
  academySlug,
  academyName: academyNameProp,
  academyLogoUrl: academyLogoUrlProp,
  clubName: clubNameProp,
  clubLogoUrl: clubLogoUrlProp,
  children,
}: DashboardLayoutProps) {
  const { currentAcademy } = useCurrentAcademy();
  const { academy: guardianAcademy } = useGuardianAcademyStore();
  const { academy: coachAcademy } = useCoachAcademyStore();
  const location = useLocation();
  const contentBodyRef = useRef<HTMLDivElement>(null);
  const topBannersRef = useRef<HTMLDivElement>(null);
  // The desktop sidebar is fixed-positioned, so banners above the shell only push
  // the content column down. --app-header-height offsets the sidebar to match.
  const [topBannersHeight, setTopBannersHeight] = useState(0);

  useEffect(() => {
    const el = topBannersRef.current;
    if (!el) return undefined;

    const measure = () => setTopBannersHeight(el.getBoundingClientRect().height);
    measure();

    if (typeof window.ResizeObserver === 'undefined') return undefined;
    const observer = new window.ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    contentBodyRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    if (dashboardRole !== 'coach' || !academySlug) return;
    void fetchOwnerStripeTierQuery(academySlug);
  }, [dashboardRole, academySlug]);

  const academyName =
    academyNameProp ??
    (dashboardRole === 'guardian' && academySlug
      ? guardianAcademy?.name
      : dashboardRole === 'coach' && academySlug
        ? coachAcademy?.name
        : currentAcademy?.name);
  const academyLogoUrl =
    academyLogoUrlProp ??
    (dashboardRole === 'guardian'
      ? guardianAcademy?.logoUrl
      : dashboardRole === 'coach'
        ? coachAcademy?.logoUrl
        : undefined);

  const unifiedNav = useUnifiedManagementNav(dashboardRole);
  const consumerNav = useUnifiedConsumerNav();

  const mobileTabItems = isOrganizationManagementRole(dashboardRole)
    ? unifiedNav.mobileTabItems
    : dashboardRole === 'coach' && academySlug
      ? getCoachMobileTabItems(academySlug)
      : dashboardRole === 'guardian' && consumerNav.useUnifiedNav
        ? consumerNav.mobileTabItems
        : dashboardRole === 'guardian' && academySlug
          ? getGuardianMobileTabItems(academySlug)
          : [];

  const showMobileTabBar = mobileTabItems.length > 0;
  const isOrgManagement = isOrganizationManagementRole(dashboardRole);

  const layout = (
    <PageHeaderProvider>
      <PageHeaderRouteReset />
      <SidebarProvider
        className="sidebar-provider flex h-svh max-h-svh min-h-0 w-full flex-col overflow-hidden"
        style={{ '--app-header-height': `${topBannersHeight}px` } as React.CSSProperties}
      >
        <div ref={topBannersRef} className="shrink-0">
          <ImpersonationBanner />
          <EmailVerificationBanner />
        </div>

        <div className="dashboard-chrome flex min-h-0 w-full flex-1 overflow-hidden">
          <DashboardSidebar
            dashboardRole={dashboardRole}
            academySlug={academySlug}
            academyName={academyName}
            academyLogoUrl={academyLogoUrl}
            clubName={clubNameProp}
            clubLogoUrl={clubLogoUrlProp}
          />

          <SidebarInset className="dashboard-main-panel flex min-h-0 w-full flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-card">
              <DashboardContentChrome dashboardRole={dashboardRole} academySlug={academySlug} />
              <InCardHeaderSlot />
              <div
                ref={contentBodyRef}
                className={cn(
                  'dashboard-content-panel-body min-h-0 flex-1',
                  showMobileTabBar && 'pb-[calc(4.75rem+env(safe-area-inset-bottom,0px))] md:pb-0'
                )}
              >
                {typeof children !== 'undefined' ? children : <Outlet />}
              </div>
            </div>

            {showMobileTabBar ? <DashboardMobileTabBar items={mobileTabItems} /> : null}
          </SidebarInset>
        </div>
      </SidebarProvider>
    </PageHeaderProvider>
  );

  if (isOrgManagement) {
    return (
      <PageTourHost>
        <ClubManagementTourHost>{layout}</ClubManagementTourHost>
      </PageTourHost>
    );
  }

  return layout;
}
